import dayjs from 'dayjs'
import capitalize from 'lodash-es/capitalize'

import { AllowedSubnets } from '@infura/key-service-sdk/lib/v1/proto/project/v1/networks'
import { SubnetType } from '@infura/key-service-sdk/lib/v1/proto/service_plan/v1/networks'
import { NetworkType } from '@infura/packages.api'
import { V1Network, V1Subnet } from '@infura/packages.uks'

/**======================
 * Types, Constants
 *========================**/

export const ENDPOINT_PROTOCOL = {
  HTTPS: 'https',
  WEBSOCKET: 'wss'
} as const
export type EndpointProtocol = (typeof ENDPOINT_PROTOCOL)[keyof typeof ENDPOINT_PROTOCOL]

export const NETWORK_FILTER = {
  EVM: 'EVM',
  HIGHER_LEVEL_API: 'HIGHER LEVEL API',
  L1: 'L1',
  L2_SIDECHAIN: 'L2 / SIDE CHAIN'
} as const
export type NetworkFilter = (typeof NETWORK_FILTER)[keyof typeof NETWORK_FILTER]

export type AllNetworks =
  | 'arbitrum'
  | 'aurora'
  | 'avalanche'
  | 'base'
  | 'blast'
  | 'binance'
  | 'celo'
  | 'eth'
  | 'ipfs'
  | 'linea'
  | 'near'
  | 'nft'
  | 'optimism'
  | 'palm'
  | 'polygon'
  | 'starknet'
  | 'zksync'

export type NetworkData = Record<string, V1Network>

export type ExpansionNetwork = 'ipfs' | 'nft'

export const ExpansionNetworkValues: Array<ExpansionNetwork> = ['ipfs', 'nft']

const NETWORK_SORT_PRIORITY: Record<AllNetworks, number> = {
  arbitrum: 7,
  aurora: 11,
  avalanche: 9,
  base: 4,
  binance: 14,
  blast: 5,
  celo: 13,
  eth: 1,
  ipfs: 15,
  linea: 2,
  near: 10,
  nft: 16,
  optimism: 6,
  palm: 8,
  polygon: 3,
  starknet: 12,
  zksync: 17
}

export type SubnetPartial = Partial<Record<keyof typeof SubnetType, any>>

/**========================================================================
 * Utility functions related to Key Service Rendering
 *========================================================================**/

/**
 *
 * @param subnet Subnet name (e.g: SUBNET_MAINNET)
 * @returns Formatted subnet name (e.g: Mainnet)
 */
export const formatSubnetName = (subnet: keyof SubnetType) => {
  return capitalize(subnet.replace('SUBNET_', '').toLowerCase())
}

/**
 * Sorts subnets with 'SUBNET_MAINNET' as the first and the rest alphabetically.
 *
 * @param subnets - An object containing subnet data to be sorted.
 *
 * @returns An array of sorted subnets.
 */
type SubnetWithKey = { subnetKey: string; subnet: V1Subnet }
export function sortSubnets(subnets: { [subnetKey: string]: V1Subnet }): Array<SubnetWithKey> {
  const { mainnet, rest } = Object.keys(subnets)
    .sort()
    .reduce(
      (
        acc: {
          mainnet: Array<SubnetWithKey>
          rest: Array<SubnetWithKey>
        },
        subnetKey
      ) => {
        if (subnetKey === 'SUBNET_MAINNET') {
          acc.mainnet.push({ subnet: subnets[subnetKey], subnetKey })
        } else {
          acc.rest.push({ subnet: subnets[subnetKey], subnetKey })
        }
        return acc
      },
      { mainnet: [], rest: [] }
    )

  return [...mainnet, ...rest]
}

/**
 * Sorts networks by priority and alphabetically.
 * Networks with a lower priority are sorted before networks with a higher priority.
 * Networks with the same priority are sorted alphabetically.
 *
 * @param networks - An array of networks to be sorted.
 *
 * @returns An array of sorted networks.
 */
export function sortNetworksByPriority(networks: Array<AllNetworks>): Array<AllNetworks> {
  return networks.sort((a, b) => {
    const priorityA = NETWORK_SORT_PRIORITY[a] || Infinity
    const priorityB = NETWORK_SORT_PRIORITY[b] || Infinity

    if (priorityA !== priorityB) {
      return priorityA - priorityB
    }

    // If priorities are equal, sort alphabetically
    return a.localeCompare(b)
  })
}

export function formatKeyUpdatedDate(timestamp: number): string {
  if (!isNaN(timestamp)) {
    const ts = timestamp * 1000
    return dayjs(ts).format('YYYY-MM-DD')
  }
  return ''
}

export function calculateActiveSubnetsCount(
  hasIpfs: boolean,
  projectNetworks: { [network: string]: AllowedSubnets },
  networkMap: { [network: string]: V1Network }
): number {
  // if IPFS is enabled, we add 1 to the count
  //
  // then fold through the project networks
  //   if network is disabled return 0
  //   if network is a dev API return 0
  //   else get the networks subnets.
  //     for each subnet
  //       if disabled add 0
  //       if enabled return 1
  let count = hasIpfs ? 1 : 0

  count += Object.entries(projectNetworks)
    .map(([networkKey, { subnets }]) => {
      const networkData = networkMap[networkKey]

      if (!networkData || networkData.disabled || networkData.developerApi || networkKey === 'ipfs') {
        return 0
      }

      return subnets.reduce((acc, subnet) => {
        const subnetName = SubnetType[subnet]
        const subnetData = networkData.subnets?.[subnetName]
        if (subnetData && !subnetData.disabled) {
          return acc + 1
        }
        return acc + 0
      }, 0)
    })
    .reduce((acc, x) => acc + x, 0)

  return count
}

export function calculateTotalSubnetsCount(networkEntries: Array<[string, V1Network]> | undefined): number {
  const totalSubnetsCount = networkEntries
    ? networkEntries.reduce((count, [, network]) => {
        if (network.disabled) {
          return count
        }

        if (network.developerApi) {
          return count
        }

        const subnetsInNetwork = Object.values(network.subnets ?? {}).filter(subnet => {
          if (subnet.disabled) {
            return false
          }
          return Boolean(subnet)
        }).length

        return count + subnetsInNetwork
      }, 0)
    : 0

  return totalSubnetsCount
}

export const getStatsQueryKeyByNetworkAndSubnet = (
  network: string,
  subnet: string,
  developerApi?: boolean
): NetworkType => {
  // @ts-expect-error enabled to support strictNullChecks - Please fix this if you can!
  const subnetFormatted = subnet.split('_').pop().toLowerCase()
  const networkFormatted = network.replace('.', '-')
  if (developerApi) {
    return `${networkFormatted}.api` as NetworkType
  }
  if (network === 'eth') {
    return `ethereum-${subnetFormatted}` as NetworkType
  }
  return `${networkFormatted}-${subnetFormatted}` as NetworkType
}
